import { X, MapPin, Layers, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { STANDARD_FIELDS } from "@/lib/featureInfo";

const parcelRows = (p) => [
  ["Khasra No.", p.khasra], ["Parcel ID", p.parcel_id], ["Owner", p.owner], ["Village", p.village], ["District", p.district],
  ["Area", `${p.area_ha} ha`], ["Land type", p.land_type], ["Survey year", p.survey_year],
  ["Coordinates", `${p.centroid?.lat}, ${p.centroid?.lng}`],
];

const SKIP = new Set(["timestamp", "begin", "end", "altitudeMode", "tessellate", "extrude", "visibility", "drawOrder", "icon", "styleUrl", "id"]);
const pretty = (k) => k.replace(/[_-]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^\w/, (c) => c.toUpperCase());
const fmt = (v) => (typeof v === "number" ? (Number.isInteger(v) ? String(v) : v.toFixed(4).replace(/\.?0+$/, "")) : String(v));

// Standard fields first (via field map / auto-detect), then every other non-empty attribute.
const featureRows = (f) => {
  const p = f.properties || {}, fm = f.fieldMap || {};
  const used = new Set(Object.values(fm));
  const rows = [];
  for (const [std, label] of STANDARD_FIELDS) {
    const src = fm[std];
    if (src && p[src] !== null && p[src] !== "") rows.push([label, std === "area_ha" ? `${fmt(p[src])} ha` : fmt(p[src])]);
    else if (std === "area_ha" && f.area_ha) rows.push([label, `${f.area_ha} ha`]);
  }
  Object.entries(p).filter(([k, v]) => !used.has(k) && !SKIP.has(k) && v !== null && v !== "" && typeof v !== "object").forEach(([k, v]) => rows.push([pretty(k), fmt(v)]));
  if (f.centroid) rows.push(["Coordinates", `${f.centroid.lat.toFixed(6)}, ${f.centroid.lng.toFixed(6)}`]);
  return rows;
};

const titleOf = (f) => {
  const p = f.properties || {}, fm = f.fieldMap || {};
  if (fm.khasra && p[fm.khasra] != null && p[fm.khasra] !== "") return `Khasra ${p[fm.khasra]}`;
  const key = Object.keys(p).find((k) => /^(name|nm|title|label)$/i.test(k)) || Object.keys(p).find((k) => /name/i.test(k));
  return key && p[key] ? String(p[key]) : f.layerName;
};

export function ParcelPanel({ parcel, onClose, onOwnerReport }) {
  if (!parcel) return null;
  const isFeature = !!parcel.layerName;
  const rows = isFeature ? featureRows(parcel) : parcelRows(parcel);
  const owner = isFeature ? (parcel.fieldMap?.owner ? parcel.properties?.[parcel.fieldMap.owner] : null) : parcel.owner;
  const mono = (k) => k === "Coordinates" || k === "Parcel ID";
  return (
    <aside data-testid="parcel-info-popup" className="absolute z-[1100] bottom-4 left-4 right-4 sm:right-auto sm:w-96 rounded-2xl backdrop-blur-xl bg-slate-900/90 text-white border border-emerald-500/30 shadow-2xl p-6 fade-up">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="data-label text-emerald-400 flex items-center gap-1.5">{isFeature ? <><Layers size={11} /> {parcel.layerName}</> : "Parcel record"}</p>
          <h3 className="mt-1 text-xl font-bold flex items-center gap-2 truncate" data-testid="parcel-info-title"><MapPin size={16} className="text-emerald-400 shrink-0" /> {isFeature ? titleOf(parcel) : `Khasra ${parcel.khasra}`}</h3>
        </div>
        <Button data-testid="parcel-info-popup-close-button" variant="ghost" size="icon" className="rounded-full text-slate-300 hover:text-white hover:bg-white/10" onClick={onClose}><X size={16} /></Button>
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm max-h-[50vh] overflow-auto pr-1">
        {rows.length === 0 && <p className="col-span-2 text-xs text-slate-400" data-testid="parcel-info-empty">This feature has no attribute data.</p>}
        {rows.map(([k, v]) => (
          <div key={k} className={k === "Coordinates" || String(v).length > 28 ? "col-span-2" : ""} data-testid="parcel-info-row">
            <dt className="text-xs text-slate-400">{k}</dt>
            <dd className={`font-medium break-words ${mono(k) ? "font-mono text-xs" : ""}`}>{v}</dd>
          </div>
        ))}
      </dl>
      {owner && onOwnerReport && (
        <button type="button" data-testid="owner-report-button" onClick={() => onOwnerReport(String(owner))}
          className="mt-4 w-full h-9 rounded-full text-xs font-semibold flex items-center justify-center gap-2 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors">
          <FileText size={13} /> All khasras of {String(owner).split(" ")[0]} · PDF
        </button>
      )}
    </aside>
  );
}
